import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2, ArrowLeft, CheckCircle2, XCircle, Sun, Moon, AlertCircle, X, FileText,
    Users, Plus, Trash2, UploadCloud, FileCheck2, Download, RefreshCw, ListChecks,
    ChevronRight, ChevronLeft as ChevronLeftIcon, Send, Star, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    useConferenceOutputRegistrationByToken,
    useRefetchOutputMetadata,
    useUpdateOutputRegistration,
    useUploadOutputCameraReady,
    useUploadOutputCopyright,
    useSubmitOutputRegistration,
    type ConferenceOutputAuthor,
} from "@/services/conferenceOutputPublicService";
import { axiosClient } from "@/lib/axiosClient";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const parseLaravelError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as any;
        if (data?.errors) {
            const firstErrorKey = Object.keys(data.errors)[0];
            const v = data.errors[firstErrorKey];
            return Array.isArray(v) ? v[0] : String(v);
        }
        if (data?.message) {
            if (typeof data.message === 'object') {
                const firstKey = Object.keys(data.message)[0];
                const v = data.message[firstKey];
                return Array.isArray(v) ? v[0] : String(v);
            }
            return String(data.message);
        }
        return "Terjadi kesalahan sistem. Silakan coba lagi.";
    }
    if (error instanceof Error) return error.message;
    return "Terjadi kesalahan yang tidak diketahui.";
};

const STEPS = [
    { key: "metadata", label: "Metadata & Penulis" },
    { key: "camera-ready", label: "Camera-Ready" },
    { key: "copyright", label: "Copyright Form" },
    { key: "checklist", label: "Checklist & Submit" },
];

export default function ConferenceOutputRegistrationPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [isDark, setIsDark] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);
    const [step, setStep] = useState(0);

    const showToast = (message: string, type: "error" | "success" = "error") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 5000);
    };

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        document.documentElement.classList.toggle("dark", newMode);
        localStorage.setItem("theme", newMode ? "dark" : "light");
    };

    const { data: registration, isLoading, isError, error } = useConferenceOutputRegistrationByToken(token);
    const refetchMetadata = useRefetchOutputMetadata(token);
    const updateRegistration = useUpdateOutputRegistration(token);
    const uploadCameraReady = useUploadOutputCameraReady(token);
    const uploadCopyright = useUploadOutputCopyright(token);
    const submitRegistration = useSubmitOutputRegistration(token);

    // Local editable form state
    const [title, setTitle] = useState("");
    const [abstract, setAbstract] = useState("");
    const [collection, setCollection] = useState("");
    const [authors, setAuthors] = useState<ConferenceOutputAuthor[]>([]);
    const [checklist1, setChecklist1] = useState(false);
    const [checklist2, setChecklist2] = useState(false);
    const [checklist3, setChecklist3] = useState(false);
    const [checklist4, setChecklist4] = useState(false);
    const [refetchDiff, setRefetchDiff] = useState<{ current: any; fresh: any } | null>(null);

    useEffect(() => {
        if (registration) {
            setTitle(registration.paper_title_final || "");
            setAbstract(registration.abstract_final || "");
            setCollection(registration.conference_proceedings_collection || "");
            setAuthors(registration.authors && registration.authors.length > 0
                ? registration.authors.map((a) => ({ ...a }))
                : [{ seq: 0, given_name: "", family_name: "", affiliation: "", email: "", orcid: "", is_corresponding: true }]);
            setChecklist1(registration.checklist_originality_similarity_below_20);
            setChecklist2(registration.checklist_metadata_matches_publisher);
            setChecklist3(registration.checklist_used_template);
            setChecklist4(registration.checklist_fee_commitment);
        }
    }, [registration]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                <p className="text-slate-500 dark:text-slate-400 font-medium tracking-wide">Memuat formulir registrasi...</p>
            </div>
        );
    }

    if (isError || !registration) {
        const isDeadlinePassed = (error as any)?.response?.status === 403 || (error as any)?.response?.data?.deadline_passed;
        const errorMessage = (error as any)?.response?.data?.message;

        if (isDeadlinePassed) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 text-center transition-colors">
                    <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mb-6">
                        <Clock className="w-10 h-10" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Batas Waktu Pengumpulan Telah Lewat</h1>
                    <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8 leading-relaxed">
                        {errorMessage || "Deadline pengumpulan naskah prosiding sudah lewat. Silakan hubungi panitia konferensi untuk permohonan perpanjangan waktu."}
                    </p>
                    <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold">Kembali ke Beranda</Button>
                </div>
            );
        }

        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 text-center transition-colors">
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-6">
                    <XCircle className="w-10 h-10" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Link Tidak Valid</h1>
                <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">Link registrasi ini tidak valid atau sudah kedaluwarsa.</p>
                <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold">Kembali ke Beranda</Button>
            </div>
        );
    }

    // Already submitted - show summary + link to billing
    if (registration.status !== "draft") {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 text-center transition-colors">
                <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Registrasi Sudah Dikirim</h1>
                <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">
                    Formulir registrasi prosiding Anda sudah disubmit sebelumnya (status: <strong>{registration.status}</strong>). Silakan cek email Anda untuk tautan tagihan pembayaran.
                </p>
                <Button onClick={() => navigate(`/conference-output/billing/${token}`)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold">Lihat Tagihan</Button>
            </div>
        );
    }

    const handleAddAuthor = () => {
        setAuthors((prev) => [...prev, { seq: prev.length, given_name: "", family_name: "", affiliation: "", email: "", orcid: "", is_corresponding: false }]);
    };

    const handleRemoveAuthor = (idx: number) => {
        setAuthors((prev) => prev.filter((_, i) => i !== idx).map((a, i) => ({ ...a, seq: i })));
    };

    const handleAuthorChange = (idx: number, field: keyof ConferenceOutputAuthor, value: string | boolean) => {
        setAuthors((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
    };

    const handleSetCorresponding = (idx: number) => {
        setAuthors((prev) => prev.map((a, i) => ({ ...a, is_corresponding: i === idx })));
    };

    const handleSaveMetadata = async () => {
        if (!title.trim()) {
            showToast("Judul paper wajib diisi.", "error");
            return;
        }
        if (authors.some((a) => !a.given_name.trim() || !a.email.trim())) {
            showToast("Nama dan email setiap penulis wajib diisi.", "error");
            return;
        }
        try {
            await updateRegistration.mutateAsync({
                paper_title_final: title,
                abstract_final: abstract,
                conference_proceedings_collection: collection,
                authors: authors.map((a, i) => ({ ...a, seq: i })),
            });
            showToast("Metadata berhasil disimpan.", "success");
            setStep(1);
        } catch (error) {
            showToast(parseLaravelError(error), "error");
        }
    };

    const handleRefetch = async () => {
        try {
            const res = await refetchMetadata.mutateAsync(false);
            setRefetchDiff(res.data);
        } catch (error) {
            showToast(parseLaravelError(error), "error");
        }
    };

    const handleApplyRefetch = async () => {
        try {
            await refetchMetadata.mutateAsync(true);
            setRefetchDiff(null);
            showToast("Data berhasil diperbarui dari sistem OJS.", "success");
        } catch (error) {
            showToast(parseLaravelError(error), "error");
        }
    };

    const handleUploadCameraReady = async (file: File) => {
        const allowedExt = [".doc", ".docx"];
        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
        if (!allowedExt.includes(ext)) {
            showToast("Format file harus DOC atau DOCX.", "error");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            showToast("Ukuran file maksimal 10 MB.", "error");
            return;
        }
        try {
            await uploadCameraReady.mutateAsync(file);
            showToast("File camera-ready berhasil diunggah.", "success");
        } catch (error) {
            showToast(parseLaravelError(error), "error");
        }
    };

    const handleUploadCopyright = async (file: File) => {
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            showToast("Format file harus PDF.", "error");
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            showToast("Ukuran file maksimal 15 MB.", "error");
            return;
        }
        try {
            await uploadCopyright.mutateAsync(file);
            showToast("File copyright form berhasil diunggah.", "success");
        } catch (error) {
            showToast(parseLaravelError(error), "error");
        }
    };

    const handleDownloadCopyrightForm = () => {
        const baseURL = axiosClient.defaults.baseURL;
        window.open(`${baseURL}/conference-output/registration/token/${encodeURIComponent(token || "")}/copyright-form.pdf`, "_blank");
    };

    const handleSubmit = async () => {
        if (!checklist1 || !checklist2 || !checklist3 || !checklist4) {
            showToast("Mohon centang keempat pernyataan checklist terlebih dahulu.", "error");
            return;
        }
        try {
            // Checkbox checklist cuma mengubah state lokal - simpan dulu ke server supaya
            // backend benar-benar tahu keempatnya sudah dicentang sebelum submit dievaluasi.
            await updateRegistration.mutateAsync({
                checklist_originality_similarity_below_20: checklist1,
                checklist_metadata_matches_publisher: checklist2,
                checklist_used_template: checklist3,
                checklist_fee_commitment: checklist4,
            });
            const res = await submitRegistration.mutateAsync();
            showToast(res.message || "Registrasi berhasil disubmit.", "success");
            setTimeout(() => navigate(`/conference-output/billing/${token}`), 1200);
        } catch (error) {
            showToast(parseLaravelError(error), "error");
        }
    };

    const canGoStep = (idx: number) => {
        if (idx === 0) return true;
        if (idx === 1) return !!title.trim() && authors.every((a) => a.given_name && a.email);
        if (idx === 2) return !!registration.camera_ready_file_path;
        if (idx === 3) return !!registration.copyright_form_file_path;
        return false;
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 py-8 px-4 transition-colors relative">
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-4 w-full max-w-md"
                    >
                        <div className={`flex items-start gap-3 p-4 rounded-xl shadow-2xl border ${toast.type === "error" ? "bg-white border-red-200 text-red-700 dark:bg-slate-900 dark:border-red-900/50 dark:text-red-400" : "bg-white border-green-200 text-green-700 dark:bg-slate-900 dark:border-green-900/50 dark:text-green-400"}`}>
                            {toast.type === "error" ? <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />}
                            <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
                            <button type="button" onClick={() => setToast(null)} className="opacity-50 hover:opacity-100 transition-opacity">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center justify-between mb-2">
                    <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
                    </button>
                    <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="rounded-full w-10 h-10 bg-slate-200/50 hover:bg-slate-300/50 dark:bg-slate-800 dark:hover:bg-slate-700 shrink-0 text-slate-700 dark:text-slate-200 transition-colors">
                        {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
                    </Button>
                </div>

                {/* Stepper */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-4 flex items-center justify-between overflow-x-auto">
                    {STEPS.map((s, idx) => (
                        <div key={s.key} className="flex items-center flex-1 min-w-[120px]">
                            <button
                                onClick={() => canGoStep(idx) && setStep(idx)}
                                disabled={!canGoStep(idx)}
                                className={`flex items-center gap-2 text-xs font-bold ${idx === step ? "text-blue-600 dark:text-blue-400" : idx < step ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"} disabled:cursor-not-allowed`}
                            >
                                <span className={`w-7 h-7 rounded-full flex items-center justify-center border-2 ${idx === step ? "border-blue-600 bg-blue-50 dark:bg-blue-900/30" : idx < step ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30" : "border-slate-300 dark:border-slate-700"}`}>
                                    {idx < step ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                                </span>
                                <span className="hidden sm:inline">{s.label}</span>
                            </button>
                            {idx < STEPS.length - 1 && <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-800 mx-2" />}
                        </div>
                    ))}
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    {/* STEP 0: Metadata + Authors */}
                    {step === 0 && (
                        <div className="p-6 md:p-8 space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-blue-600" /> Metadata Paper
                                </h2>
                                <Button variant="outline" size="sm" onClick={handleRefetch} disabled={refetchMetadata.isPending} className="rounded-xl">
                                    {refetchMetadata.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                    Ambil Ulang dari OJS
                                </Button>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm font-semibold">Judul Paper <span className="text-red-500">*</span></Label>
                                <Textarea value={title} onChange={(e) => setTitle(e.target.value)} className="min-h-[80px]" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold">Abstrak</Label>
                                <Textarea value={abstract} onChange={(e) => setAbstract(e.target.value)} className="min-h-[120px]" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold">Conference Proceedings Collection</Label>
                                <Input value={collection} onChange={(e) => setCollection(e.target.value)} placeholder="Nama koleksi/track prosiding" />
                            </div>

                            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                        <Users className="w-4 h-4" /> Daftar Penulis
                                    </h3>
                                    <Button variant="outline" size="sm" onClick={handleAddAuthor} className="rounded-xl">
                                        <Plus className="w-4 h-4 mr-1" /> Tambah Penulis
                                    </Button>
                                </div>
                                <div className="space-y-4">
                                    {authors.map((author, idx) => (
                                        <div key={idx} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-slate-400 uppercase">Penulis {idx + 1}</span>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetCorresponding(idx)}
                                                        className={`flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md ${author.is_corresponding ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"}`}
                                                    >
                                                        <Star className="w-3 h-3" /> {author.is_corresponding ? "Corresponding" : "Jadikan Corresponding"}
                                                    </button>
                                                    {authors.length > 1 && (
                                                        <button type="button" onClick={() => handleRemoveAuthor(idx)} className="text-red-500 hover:text-red-600">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid sm:grid-cols-2 gap-3">
                                                <Input placeholder="Nama depan *" value={author.given_name} onChange={(e) => handleAuthorChange(idx, "given_name", e.target.value)} />
                                                <Input placeholder="Nama belakang" value={author.family_name || ""} onChange={(e) => handleAuthorChange(idx, "family_name", e.target.value)} />
                                                <Input placeholder="Email *" type="email" value={author.email} onChange={(e) => handleAuthorChange(idx, "email", e.target.value)} />
                                                <Input placeholder="ORCID" value={author.orcid || ""} onChange={(e) => handleAuthorChange(idx, "orcid", e.target.value)} />
                                                <Input placeholder="Afiliasi" value={author.affiliation || ""} onChange={(e) => handleAuthorChange(idx, "affiliation", e.target.value)} className="sm:col-span-2" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button onClick={handleSaveMetadata} disabled={updateRegistration.isPending} className="rounded-xl h-11 px-6 bg-blue-600 hover:bg-blue-700">
                                    {updateRegistration.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    Simpan & Lanjut <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* STEP 1: Camera-Ready */}
                    {step === 1 && (
                        <div className="p-6 md:p-8 space-y-6">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <UploadCloud className="w-5 h-5 text-blue-600" /> Unggah File Camera-Ready
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Unggah naskah final (camera-ready) sesuai template {registration.publisher_info?.display_name || "publisher"}. Format: DOC atau DOCX, maksimal 10 MB.</p>

                            {(registration.publisher_info?.template_file_path || registration.publisher_info?.guideline_file_path) && (
                                <div className="flex flex-wrap gap-3">
                                    {registration.publisher_info.template_file_path && (
                                        <a href={registration.publisher_info.template_file_path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl px-4 py-2.5 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                                            <Download className="w-4 h-4" /> Unduh Template Naskah
                                        </a>
                                    )}
                                    {registration.publisher_info.guideline_file_path && (
                                        <a href={registration.publisher_info.guideline_file_path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl px-4 py-2.5 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                                            <Download className="w-4 h-4" /> Unduh Author Guideline
                                        </a>
                                    )}
                                </div>
                            )}

                            {registration.camera_ready_file_path ? (
                                <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl">
                                    <FileCheck2 className="w-6 h-6 text-emerald-600 shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-emerald-800 dark:text-emerald-400">File berhasil diunggah</p>
                                        <a href={registration.camera_ready_file_path} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 underline">Lihat file</a>
                                    </div>
                                </div>
                            ) : null}

                            <label className="block border-2 border-dashed border-blue-200 dark:border-blue-800/50 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-400 transition-colors">
                                <UploadCloud className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                                <p className="font-semibold text-slate-700 dark:text-slate-200">{uploadCameraReady.isPending ? "Mengunggah..." : "Klik untuk pilih file"}</p>
                                <p className="text-xs text-slate-500 mt-1">DOC, DOCX - maks 10 MB</p>
                                <input
                                    type="file"
                                    accept=".doc,.docx"
                                    className="hidden"
                                    disabled={uploadCameraReady.isPending}
                                    onChange={(e) => e.target.files?.[0] && handleUploadCameraReady(e.target.files[0])}
                                />
                            </label>

                            <div className="flex justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="outline" onClick={() => setStep(0)} className="rounded-xl h-11 px-6">
                                    <ChevronLeftIcon className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <Button onClick={() => setStep(2)} disabled={!registration.camera_ready_file_path} className="rounded-xl h-11 px-6 bg-blue-600 hover:bg-blue-700">
                                    Lanjut <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: Copyright */}
                    {step === 2 && (
                        <div className="p-6 md:p-8 space-y-6">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <FileCheck2 className="w-5 h-5 text-blue-600" /> Copyright Transfer Form
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Unduh formulir copyright form yang sudah terisi otomatis, tanda tangani secara manual (basah/digital), lalu unggah kembali di sini.
                            </p>

                            <Button onClick={handleDownloadCopyrightForm} variant="outline" className="w-full h-12 rounded-2xl border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 font-bold">
                                <Download className="w-4 h-4 mr-2" /> Unduh Copyright Form (PDF)
                            </Button>

                            {registration.copyright_form_file_path ? (
                                <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl">
                                    <FileCheck2 className="w-6 h-6 text-emerald-600 shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-emerald-800 dark:text-emerald-400">File yang sudah ditandatangani berhasil diunggah</p>
                                        <a href={registration.copyright_form_file_path} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 underline">Lihat file</a>
                                    </div>
                                </div>
                            ) : null}

                            <label className="block border-2 border-dashed border-blue-200 dark:border-blue-800/50 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-400 transition-colors">
                                <UploadCloud className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                                <p className="font-semibold text-slate-700 dark:text-slate-200">{uploadCopyright.isPending ? "Mengunggah..." : "Klik untuk unggah copyright form yang sudah ditandatangani"}</p>
                                <p className="text-xs text-slate-500 mt-1">PDF - maks 15 MB</p>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    disabled={uploadCopyright.isPending}
                                    onChange={(e) => e.target.files?.[0] && handleUploadCopyright(e.target.files[0])}
                                />
                            </label>

                            <div className="flex justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl h-11 px-6">
                                    <ChevronLeftIcon className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <Button onClick={() => setStep(3)} disabled={!registration.copyright_form_file_path} className="rounded-xl h-11 px-6 bg-blue-600 hover:bg-blue-700">
                                    Lanjut <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: Checklist + Submit */}
                    {step === 3 && (
                        <div className="p-6 md:p-8 space-y-6">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <ListChecks className="w-5 h-5 text-blue-600" /> Checklist Akhir
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4">Before completing your camera-ready submission, please confirm the following:</p>

                            <div className="space-y-3">
                                <label className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl cursor-pointer">
                                    <Checkbox checked={checklist3} onCheckedChange={(v) => setChecklist3(!!v)} className="mt-0.5" />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">
                                        <span className="font-bold block mb-0.5">Paper Formatting</span>
                                        The uploaded camera-ready paper has been carefully prepared and formatted in accordance with the publisher's official template and formatting guidelines.
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl cursor-pointer">
                                    <Checkbox checked={checklist1} onCheckedChange={(v) => setChecklist1(!!v)} className="mt-0.5" />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">
                                        <span className="font-bold block mb-0.5">Plagiarism Check</span>
                                        The paper has been checked for plagiarism/similarity, and the similarity index does not exceed 20%.
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl cursor-pointer">
                                    <Checkbox checked={checklist2} onCheckedChange={(v) => setChecklist2(!!v)} className="mt-0.5" />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">
                                        <span className="font-bold block mb-0.5">Metadata Accuracy</span>
                                        All metadata entered in the submission system, including the article title, author names, affiliations, and other author information, is accurate and fully consistent with the information presented in the uploaded article.
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl cursor-pointer">
                                    <Checkbox checked={checklist4} onCheckedChange={(v) => setChecklist4(!!v)} className="mt-0.5" />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">
                                        <span className="font-bold block mb-0.5">Publication Fee Commitment</span>
                                        I agree to complete the payment of the proceedings publication fee of{" "}
                                        <strong>
                                            {registration.fee_info
                                                ? new Intl.NumberFormat(registration.fee_info.currency === "USD" ? "en-US" : "id-ID", { style: "currency", currency: registration.fee_info.currency, minimumFractionDigits: 0 }).format(Number(registration.fee_info.amount))
                                                : "the applicable amount"}
                                        </strong>{" "}
                                        in accordance with the payment instructions provided by the conference organizer.
                                    </span>
                                </label>
                            </div>

                            <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-2xl p-4 text-sm text-blue-800 dark:text-blue-300">
                                Setelah disubmit, sistem akan membuat tagihan pembayaran biaya publikasi dan mengirimkan link tagihan ke email Anda. Registrasi tidak dapat diubah lagi setelah disubmit.
                            </div>

                            <div className="flex justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl h-11 px-6">
                                    <ChevronLeftIcon className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={submitRegistration.isPending || updateRegistration.isPending || !checklist1 || !checklist2 || !checklist3 || !checklist4}
                                    className="rounded-xl h-11 px-8 bg-emerald-600 hover:bg-emerald-700 font-bold"
                                >
                                    {(submitRegistration.isPending || updateRegistration.isPending) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    Submit Registrasi
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="text-center text-xs text-slate-400 dark:text-slate-500 pb-10">
                    <p>Jika ada pertanyaan, silakan hubungi panitia KONASPI XII.</p>
                </div>
            </div>

            {/* Dialog Perbandingan Data OJS - tampilkan dulu sebelum menimpa data yang mungkin sudah disunting penulis */}
            <Dialog open={!!refetchDiff} onOpenChange={() => setRefetchDiff(null)}>
                <DialogContent className="sm:max-w-[560px] bg-white dark:bg-slate-900 rounded-3xl p-0 overflow-hidden">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-blue-600" /> Perbandingan Data OJS</DialogTitle>
                        <DialogDescription>
                            Berikut data terbaru dari sistem OJS dibandingkan dengan data yang sedang Anda sunting. Menerapkan perubahan ini akan menimpa judul, abstrak, dan daftar penulis di formulir.
                        </DialogDescription>
                    </DialogHeader>

                    {refetchDiff && (
                        <div className="p-6 space-y-5 max-h-[55vh] overflow-y-auto">
                            <DiffField
                                label="Judul Paper"
                                current={refetchDiff.current.title}
                                fresh={refetchDiff.fresh.title}
                            />
                            <DiffField
                                label="Abstrak"
                                current={refetchDiff.current.abstract}
                                fresh={refetchDiff.fresh.abstract}
                            />
                            <DiffField
                                label="Daftar Penulis"
                                current={(refetchDiff.current.authors || []).map((a: any) => `${a.given_name} ${a.family_name || ""}`.trim()).join(", ") || "-"}
                                fresh={(refetchDiff.fresh.authors || []).map((a: any) => `${a.given_name} ${a.family_name || ""}`.trim()).join(", ") || "-"}
                            />
                        </div>
                    )}

                    <DialogFooter className="p-6 pt-0">
                        <Button variant="outline" onClick={() => setRefetchDiff(null)} className="rounded-xl">Batal, Tetap Pakai Data Saat Ini</Button>
                        <Button onClick={handleApplyRefetch} disabled={refetchMetadata.isPending} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                            {refetchMetadata.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Terapkan Perubahan Ini
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DiffField({ label, current, fresh }: { label: string; current: string; fresh: string }) {
    const isSame = (current || "").trim() === (fresh || "").trim();
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</span>
                {isSame ? (
                    <span className="text-[10px] font-bold text-slate-400">Tidak ada perubahan</span>
                ) : (
                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">Berbeda</span>
                )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Data Saat Ini</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">{current || "-"}</p>
                </div>
                <div className={cn("border rounded-xl p-3", isSame ? "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700" : "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/40")}>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Data Terbaru dari OJS</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">{fresh || "-"}</p>
                </div>
            </div>
        </div>
    );
}
